// Single source of truth for the four sellable products: their display names, the
// marketing copy shown on the landing page and in the product catalogue, and the
// entitlement rules that decide which products a signed-in buyer may still purchase.
// Numeric ids mirror the backend `Products` enum (NaMedinu.Entities/Enums/Products.cs).
export const Products = {
    SelfStudy: 0,
    Course: 1,
    Individual: 2,
    ExtraHours: 3,
} as const;

export const productNames: { [key: number]: string } = {
    [Products.SelfStudy]: "Samostudium",
    [Products.Course]: "Kurz s lektorem",
    [Products.Individual]: "Individuální lekce",
    [Products.ExtraHours]: "Hodiny navíc",
};

export type ProductInfo = {
    tagline: string;
    // Short punchy line for the landing cards.
    lead: string;
    // Longer paragraph for the catalogue cards.
    desc: string;
    features: string[];
    recommended?: boolean;
    ribbon?: string;
};

// One canonical copy per product, rendered by both the landing page and the
// catalogue so the two can never drift (they used to keep separate, diverging lists).
export const productInfo: { [key: number]: ProductInfo } = {
    [Products.Course]: {
        tagline: "Vedená příprava s lektorem",
        lead: "Naše prémiová příprava od začátku až do konce. Živá výuka v malých skupinkách a kompletní materiály na jednom místě.",
        desc: "Maximum podpory a struktury na cestě k přijetí. 128 živých online lekcí v malé skupině, kde se Ti lektor opravdu věnuje.",
        features: [
            "128 živých online lekcí s lektorem",
            "Malé skupiny (max. 8 studentů)",
            "Vše ze Samostudia v ceně",
            "Příprava i na ústní pohovory",
        ],
        recommended: true,
        ribbon: "NEJOBLÍBENĚJŠÍ",
    },
    [Products.Individual]: {
        tagline: "Lekce 1:1 na míru",
        lead: "Výuka jen pro Tebe. Tempo, témata i termíny si určuješ sám.",
        desc: "Individuální online lekce 1:1 přizpůsobené přesně Tvým slabinám a tempu — 96 osobních lekcí jen pro Tebe. Platíš pohodlně každý měsíc.",
        features: [
            "96 osobních lekcí 1:1 s lektorem",
            "Plán přípravy na míru",
            "Vše ze Samostudia v ceně",
            "Flexibilní měsíční platba",
        ],
    },
    [Products.SelfStudy]: {
        tagline: "Uč se vlastním tempem",
        lead: "Plný přístup ke všem materiálům. Uč se kdykoliv a kdekoliv vlastním tempem.",
        desc: "Kompletní příprava pro samostatné studium. Projdi si veškeré materiály i otázky kdykoli se Ti to hodí — z mobilu i počítače.",
        features: [
            "Studijní materiály ke všem 3 předmětům",
            "Videa s řešenými příklady",
            "Testové otázky s vysvětlením",
            "Modelové testy a statistiky",
        ],
    },
    [Products.ExtraHours]: {
        tagline: "Když potřebuješ přidat",
        lead: "Potřebuješ doladit detaily? Dokup si jednotlivé lekce s lektorem, kdykoliv je potřebuješ.",
        desc: "Dokup si jednotlivé lekce navíc, když chceš víc procvičit konkrétní téma. Bez dlouhodobého závazku.",
        features: [
            "Jednotlivé lekce dle potřeby",
            "Rezervace termínů online",
            "Bez dlouhodobého závazku",
        ],
    },
};

// Landing-page display order — the featured course first, extras last.
export const landingOrder = [Products.Course, Products.Individual, Products.SelfStudy, Products.ExtraHours];

// Products that can be bought repeatedly (extra lessons top up a credit balance),
// so owning one never locks its card.
export const REPEATABLE_PRODUCTS: number[] = [Products.ExtraHours];

// What each product includes: the lecturer course bundles the self-study materials and
// supersedes individual tutoring; individual tutoring also bundles the materials. Owning
// a covering product therefore locks the products it already grants. Mirrors the
// authoritative backend guard OrdersService.AlreadyEntitled.
const PRODUCT_GRANTS: { [key: number]: number[] } = {
    [Products.SelfStudy]: [Products.SelfStudy],
    [Products.Individual]: [Products.SelfStudy, Products.Individual],
    [Products.Course]: [Products.SelfStudy, Products.Individual, Products.Course],
    [Products.ExtraHours]: [Products.ExtraHours],
};

// True when the buyer already owns this product, or owns something that includes it, so
// it should not be offered for (re)purchase. Extra hours are always buyable.
export function isProductLocked(product: number, ownedProducts: number[]): boolean {
    if (REPEATABLE_PRODUCTS.includes(product)) return false;
    return ownedProducts.some((owned) => (PRODUCT_GRANTS[owned] ?? [owned]).includes(product));
}
